import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {SimCReport, Reports, Character} from '../types/simc';
import simcService from '../services/simc';
import { useMemo } from 'react';

export const useSimCReports = () => {
    const queryClient = useQueryClient();

    const {
        data = [],
        isLoading,
        error,
        refetch
    } = useQuery<SimCReport[]>({
        queryKey: ['simc-reports'],
        queryFn: () => simcService.getReports(),
        staleTime: 30 * 1000, // 30 seconds
    });

    // Group reports by Discord user
    const groupedReports: Reports[] = useMemo(() => {
        const groupMap = new Map<string, Map<string, SimCReport[]>>();

        data.forEach((report: SimCReport) => {
            if (!groupMap.has(report.discord_id)) {
                groupMap.set(report.discord_id, new Map());
            }

            const characterMap = groupMap.get(report.discord_id)!;
            if (!characterMap.has(report.character)) {
                characterMap.set(report.character, []);
            }

            characterMap.get(report.character)!.push(report);
        });

        return Array.from(groupMap.entries())
            .map(([discord_id, characterMap]) => ({
            discord_id,
            characters: Array.from(characterMap.entries())
                .map(([name, reports]) => ({
                name,
                reports: reports.sort((a, b) => b.ts_ms - a.ts_ms),
            })).sort(({ name: a }, { name: b }) => a.localeCompare(b))
            }));
    }, [data]);

    const uploadMutation = useMutation({
        mutationFn: async (reportData: string) => {
            return simcService.uploadReport(reportData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['simc-reports'] });
        }
    });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['simc-reports'] });
    };

    const setNoteMutation = useMutation({
        mutationFn: async (report: SimCReport & { note: string }) => {
            return simcService.setReportNote(report.id, report.note);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['simc-reports']});
        }
    });

    const deleteMutation = useMutation({
        mutationFn: simcService.deleteReport,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['simc-reports'] });
        }
    });

    return {
        reports: data,
        groupedReports,
        isLoading,
        error,
        refetch,
        invalidate,
        updateNote: setNoteMutation.mutate,
        uploadReport: uploadMutation.mutate,
        deleteReport: deleteMutation.mutate,
        isUploading: uploadMutation.isPending,
        uploadError: uploadMutation.error?.message
    };
};
